import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Film, MessageSquare, Truck, Newspaper, CalendarDays, Clapperboard, Trophy, Coins, Megaphone, BarChart3, Search, Bell, ChevronDown, LogOut, Gem, ArrowRight, User, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge, SubmitVideoDialog } from "@/components/dashboard-parts";
import GroupForum from "@/components/GroupForum";
import MemberNews from "@/components/MemberNews";
import MemberTracking from "@/components/MemberTracking";
import CalendarPanel from "@/components/CalendarPanel";
import VideoblogPanel from "@/components/VideoblogPanel";
import AwardsPanel from "@/components/AwardsPanel";
import LeaderboardPanel from "@/components/LeaderboardPanel";
import PointsPanel from "@/components/PointsPanel";
import CampaignPanel from "@/components/CampaignPanel";
import HelpPanel from "@/components/HelpPanel";
import ProfilePanel from "@/components/ProfilePanel";
import NotificationBell from "@/components/NotificationBell";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { id: "campaign", label: "Campaign", icon: Megaphone },
  { id: "tracking", label: "Tracking Order", icon: Truck },
  { id: "submissions", label: "Video Saya", icon: Film },
  { id: "news", label: "News", icon: Newspaper },
  { id: "calendar", label: "Calendar", icon: CalendarDays },
  { id: "videoblog", label: "Videoblog", icon: Clapperboard },
  { id: "awards", label: "Awards", icon: Trophy },
  { id: "leaderboard", label: "Papan Juara", icon: BarChart3 },
  { id: "forum", label: "Forum", icon: MessageSquare },
  { id: "points", label: "My Points", icon: Coins, divider: true },
  { id: "help", label: "Bantuan / FAQ", icon: HelpCircle },
  { id: "profile", label: "My Profile", icon: User },
];

const nf = (n) => (Number(n) || 0).toLocaleString("id-ID");

export default function MemberDashboard({ role, demo }) {
  const { user, logout } = useAuth();
  const isCreator = role === "beauty_creator";
  const [active, setActive] = useState("campaign");
  const [submissions, setSubmissions] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [points, setPoints] = useState(demo ? 1250 : 0);
  const displayName = user?.name || (demo ? "Rara Beauty" : "Member");

  const loadSubs = useCallback(async () => { if (demo) return; try { setSubmissions((await api.get("/submissions")).data); } catch {} }, [demo]);
  const loadPoints = useCallback(async () => { if (demo) return; try { setPoints((await api.get("/points/summary")).data.reward_pooled); } catch {} }, [demo]);
  useEffect(() => { loadSubs(); loadPoints(); }, [loadSubs, loadPoints]);

  const trackingItems = participations.filter((p) => p.status === "approved").map((p) => ({
    id: p.id, product_name: p.campaign_title, product_image: p.product_image, status: "approved",
    courier: p.courier, tracking_number: p.tracking_number, tracking_status: p.tracking_status, tracking_updated_at: p.tracking_updated_at,
  }));

  const current = NAV.find((n) => n.id === active) || NAV[0];

  return (
    <div className="min-h-screen bg-showty-cream showty-grain flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[#fdf6ec] border-r border-border sticky top-0 h-screen" data-testid="creator-sidebar">
        <div className="px-6 py-6 flex items-center gap-2.5">
          <Logo className="w-9 h-9" />
          <div>
            <p className="font-display text-2xl leading-none text-showty-navy">Showtynion</p>
            <p className="text-[10px] tracking-[0.2em] uppercase text-showty-red mt-1">{isCreator ? "Beauty Creator" : "Affiliator"}</p>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {NAV.map((n) => (
            <React.Fragment key={n.id}>
              {n.divider && <div className="my-3 border-t border-border/70 mx-3" />}
              <button onClick={() => setActive(n.id)} data-testid={`nav-${n.id}`}
                className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all ${active === n.id ? "bg-showty-red text-white shadow-sm" : "text-showty-navy/80 hover:bg-showty-pink/40"}`}>
                <n.icon className="w-4 h-4" /> {n.label}
              </button>
            </React.Fragment>
          ))}
        </nav>
        <p className="px-6 py-5 font-display text-lg text-showty-red/70 leading-tight italic">Where beauty<br />meet community</p>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-showty-cream/85 backdrop-blur-xl border-b border-border px-4 sm:px-8 py-3.5 flex items-center gap-3">
          <div className="relative flex-1 max-w-2xl">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input placeholder="Cari campaign, produk, tips, atau topik..." data-testid="creator-search"
              className="w-full h-11 rounded-full bg-white border border-border pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-showty-red/30" />
          </div>
          {demo && <button className="relative w-11 h-11 rounded-full bg-white border border-border flex items-center justify-center text-showty-navy hover:bg-showty-pink/40" data-testid="creator-bell">
            <Bell className="w-5 h-5" /><span className="absolute top-2.5 right-2.5 w-2 h-2 rounded-full bg-showty-red" />
          </button>}
          {!demo && <NotificationBell member />}
          <div className="flex items-center gap-2.5 pl-1">
            {user?.picture ? <img src={user.picture} alt="" className="w-10 h-10 rounded-full object-cover border border-border" /> : <div className="w-10 h-10 rounded-full bg-showty-pink flex items-center justify-center text-showty-red font-semibold">{displayName[0]}</div>}
            <div className="hidden sm:block leading-tight">
              <p className="text-sm font-semibold text-showty-navy">{displayName}</p>
              <p className="text-xs text-muted-foreground">{isCreator ? "Creator Member" : "Affiliator Member"}</p>
            </div>
            <button onClick={logout} className="text-muted-foreground hover:text-showty-red" data-testid="logout-btn"><LogOut className="w-4 h-4" /></button>
          </div>
        </header>

        {/* Mobile nav */}
        <div className="lg:hidden flex gap-1 overflow-x-auto px-3 py-2 border-b border-border bg-showty-cream/90">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setActive(n.id)} className={`shrink-0 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${active === n.id ? "bg-showty-red text-white" : "text-showty-navy/80 bg-white border border-border"}`}>
              <n.icon className="w-3.5 h-3.5" /> {n.label}
            </button>
          ))}
        </div>

        <main className="flex-1 px-4 sm:px-8 py-6 max-w-[1400px] w-full">
          {active === "campaign" ? (
            <>
              <HeroBanner isCreator={isCreator} points={points} onJoin={() => {}} />
              <CampaignPanel onData={setParticipations} demo={demo} />
            </>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="font-display text-3xl sm:text-4xl text-showty-navy flex items-center gap-2.5"><current.icon className="w-7 h-7 text-showty-red" /> {current.label}</h1>
              </div>
              {active === "tracking" && <MemberTracking requests={trackingItems} demo={demo} />}
              {active === "points" && <PointsPanel />}
              {active === "news" && <MemberNews />}
              {active === "calendar" && <CalendarPanel />}
              {active === "videoblog" && <VideoblogPanel />}
              {active === "awards" && <AwardsPanel />}
              {active === "leaderboard" && <LeaderboardPanel />}
              {active === "forum" && <GroupForum channel={role} />}
              {active === "help" && <HelpPanel />}
              {active === "profile" && <ProfilePanel isCreator={isCreator} />}
              {active === "submissions" && (
                <>
                  <div className="flex justify-end mb-4"><SubmitVideoDialog role={role} onDone={loadSubs} /></div>
                  {submissions.length === 0 ? <Empty text="Belum ada video disubmit" /> : (
                    <div className="space-y-3">
                      {submissions.map((s) => (
                        <div key={s.id} className="rounded-xl border border-border bg-white p-4" data-testid={`sub-row-${s.id}`}>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0"><p className="font-medium text-showty-navy truncate">{s.product_name}</p><p className="text-xs text-muted-foreground">{s.platform} · {s.video_links?.length || 0} link</p></div>
                            <StatusBadge status={s.status} />
                          </div>
                          {s.video_links?.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{s.video_links.map((l, i) => <a key={i} href={l} target="_blank" rel="noreferrer" className="text-xs text-showty-red underline underline-offset-2 truncate max-w-[220px]">{l}</a>)}</div>}
                          {s.review_note && <p className="mt-2 text-xs text-muted-foreground italic">Catatan manager: {s.review_note}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const HeroBanner = ({ isCreator, points }) => (
  <div data-testid="creator-hero">
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-showty-pink/60 via-showty-cream to-showty-pink/40 border border-border mb-5">
      <div className="grid md:grid-cols-2 items-center">
        <div className="p-8 lg:p-10 relative z-10">
          <h1 className="font-display text-4xl lg:text-5xl text-showty-navy leading-tight">{isCreator ? "Beauty Creator Studio" : "Affiliator Hub"}</h1>
          <p className="mt-3 text-sm text-muted-foreground max-w-md">Ikuti campaign eksklusif Showty, produksi konten sesuai SOW, upload bukti post untuk approval & kumpulkan point.</p>
        </div>
        <div className="relative h-40 md:h-56">
          <img src="/images/hero.png" alt="" className="absolute inset-0 w-full h-full object-cover object-top" onError={(e) => { e.currentTarget.src = "/images/brand-products.webp"; }} />
          <div className="absolute inset-0 bg-gradient-to-r from-showty-cream via-transparent to-transparent" />
        </div>
      </div>
      <div className="absolute top-5 right-5 z-20 hidden sm:flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur border border-border px-4 py-2.5 shadow-sm" data-testid="hero-points">
        <Gem className="w-6 h-6 text-showty-red" />
        <div><p className="text-[10px] uppercase tracking-wide text-muted-foreground">Your Points</p><p className="font-display text-2xl text-showty-navy leading-none">{nf(points)} <span className="text-sm">pts</span></p></div>
        <ArrowRight className="w-4 h-4 text-showty-red" />
      </div>
    </div>
    <div className="relative overflow-hidden rounded-2xl bg-showty-red text-white px-6 lg:px-8 py-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="promo-banner">
      <div className="flex items-center gap-5">
        <p className="font-display text-xl lg:text-2xl leading-tight max-w-[220px]">CREATE BEAUTY STORIES TOGETHER</p>
        <div className="h-12 w-px bg-white/30 hidden sm:block" />
        <p className="text-sm text-white/85 max-w-xs hidden sm:block">Get exclusive products, create content, share your voice, and grow with Showtynion.</p>
      </div>
      <div className="text-right hidden lg:block">
        <p className="font-display text-2xl tracking-wide">SHOWTYNION</p>
        <p className="text-[10px] tracking-[0.25em] text-white/70">BEAUTY CREATORS COMMUNITY</p>
      </div>
    </div>
  </div>
);

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</div>
);
